/**
 * Custom single-select dropdown. Replaces SlimSelect — no external dependencies.
 * Wraps a native <select> element (hidden, stays in DOM for form submission).
 */

import { t } from "../utils/I18n";

const CHEVRON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 4.5L6 7.5L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const CHECK_SVG   = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6.5L4.5 9L10 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

export interface SelectOptions {
  placeholder?: string;
  searchPlaceholder?: string;
  openPosition?: "auto" | "up" | "down";
  onChange?: (value: string, label: string) => void;
  emptyMessage?: string;
  /** 검색창 표시 여부. 미지정(undefined) 시 옵션 5개 초과면 자동 표시. */
  searchable?: boolean;
}

interface Item { value: string; label: string; sublabel?: string; flagIso?: string; disabled: boolean; }

let _uid = 0;

export class Select {
  private readonly el: HTMLSelectElement;
  private readonly opts: SelectOptions;
  private container!: HTMLDivElement;
  private trigger!: HTMLButtonElement;
  private triggerVal!: HTMLSpanElement;
  private dropdown!: HTMLDivElement;
  private searchInput?: HTMLInputElement;
  private listEl!: HTMLUListElement;
  private allItems: Item[] = [];
  private isOpen = false;
  private activeIdx = -1;
  private readonly uid: string;

  constructor(target: string | HTMLSelectElement, opts: SelectOptions = {}) {
    const el = typeof target === "string"
      ? document.querySelector<HTMLSelectElement>(target)
      : target;
    if (!el) throw new Error("Select: element not found");
    this.el = el;
    this.opts = opts;
    this.uid = `cs${++_uid}`;
  }

  init(): this {
    if (this.el.dataset.csInited) return this;
    this._parse();
    this._build();
    this._wire();
    this.el.dataset.csInited = "1";
    return this;
  }

  getValue(): string { return this.el.value; }

  setValue(v: string): void {
    if (!Array.from(this.el.options).find(o => o.value === v)) return;
    this.el.value = v;
    this._updateLabel();
    this._refreshList();
    this.el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  destroy(): void {
    document.removeEventListener("click", this._onDocClick, true);
    window.removeEventListener("scroll", this._onReposition, true);
    window.removeEventListener("resize", this._onReposition);
    // 열린 상태에서 body 로 포탈된 dropdown 도 확실히 제거
    this.dropdown?.remove();
    this.container?.remove();
    this.el.style.display = "";
    delete this.el.dataset.csInited;
  }

  private _parse(): void {
    this.allItems = Array.from(this.el.options)
      .filter(o => !(o.value === "" && (o.hasAttribute("data-placeholder") || o.text.trim() === "")))
      .map(o => ({ value: o.value, label: o.text.trim(), sublabel: o.dataset.sublabel, flagIso: o.dataset.flagIso, disabled: o.disabled }));
  }

  private _ph(): string {
    return this.opts.placeholder ?? this.el.dataset.placeholder ?? t("select.placeholder");
  }

  /** 검색창 표시 여부 — 명시 옵션 우선, 미지정 시 옵션 5개 초과면 자동 표시. */
  private _searchable(): boolean {
    return typeof this.opts.searchable === "boolean"
      ? this.opts.searchable
      : this.allItems.length > 5;
  }

  private _build(): void {
    this.el.style.display = "none";
    this.container = mk("div", { class: "cs-wrap" });

    // Trigger button
    this.trigger = mk("button", {
      type: "button", class: "cs-trigger",
      role: "combobox", "aria-haspopup": "listbox",
      "aria-expanded": "false", "aria-controls": `${this.uid}-lb`,
    }) as HTMLButtonElement;
    this.triggerVal = mk("span", { class: "cs-trigger__value" });
    const arrow = mk("span", { class: "cs-trigger__arrow", "aria-hidden": "true" });
    arrow.innerHTML = CHEVRON_SVG;
    this.trigger.append(this.triggerVal, arrow);
    this._updateLabel();

    // Dropdown panel
    this.dropdown = mk("div", {
      class: "cs-dropdown", id: `${this.uid}-lb`,
    }) as HTMLDivElement;
    this.dropdown.hidden = true;

    // Search — 명시 searchable 옵션 우선, 미지정 시 옵션 5개 초과면 자동 표시
    if (this._searchable()) {
      const wrap = mk("div", { class: "cs-dropdown__search" });
      this.searchInput = mk("input", {
        type: "text", class: "cs-dropdown__search-input",
        placeholder: this.opts.searchPlaceholder ?? t("select.search"),
        autocomplete: "off", "aria-label": t("select.search_aria"),
      }) as HTMLInputElement;
      wrap.appendChild(this.searchInput);
      this.dropdown.appendChild(wrap);
    }

    this.listEl = mk("ul", {
      class: "cs-list", role: "listbox", "data-lenis-prevent": "",
    }) as HTMLUListElement;
    this.dropdown.appendChild(this.listEl);
    this._buildList(this.allItems);

    this.container.append(this.trigger, this.dropdown);
    this.el.insertAdjacentElement("afterend", this.container);
  }

  private _updateLabel(): void {
    // placeholder 판별식은 _parse()와 동일하게 유지 — value="" 라도 텍스트가 있고
    // data-placeholder가 없으면(예: "전체 …" 필터 옵션) 정상 선택값으로 트리거에 표시.
    const isPlaceholder = (o: HTMLOptionElement) =>
      o.value === "" && (o.hasAttribute("data-placeholder") || o.text.trim() === "");
    const sel = Array.from(this.el.options).find(o => o.selected && !isPlaceholder(o));
    this.triggerVal.innerHTML = "";
    if (sel) {
      const flagIso = sel.dataset.flagIso;
      if (flagIso) {
        const flag = mk("span", { class: `locale-flag locale-flag--${flagIso}` });
        this.triggerVal.appendChild(flag);
      }
      this.triggerVal.appendChild(document.createTextNode(sel.text.trim()));
      this.triggerVal.classList.remove("cs-trigger__value--empty");
    } else {
      this.triggerVal.textContent = this._ph();
      this.triggerVal.classList.add("cs-trigger__value--empty");
    }
  }

  private _buildList(items: Item[]): void {
    this.listEl.innerHTML = "";
    const cur = this.el.value;

    if (!items.length) {
      const li = mk("li", { class: "cs-option cs-option--empty" });
      li.textContent = this.opts.emptyMessage ?? t("select.empty");
      this.listEl.appendChild(li);
      this.activeIdx = -1;
      return;
    }

    items.forEach(item => {
      const sel = item.value === cur;
      const cls = ["cs-option", sel && "cs-option--selected", item.disabled && "cs-option--disabled"]
        .filter(Boolean).join(" ");
      const li = mk("li", {
        class: cls, role: "option", "data-value": item.value,
        "aria-selected": String(sel),
        ...(item.disabled ? { "aria-disabled": "true" } : {}),
      });
      const lbl = mk("span", { class: "cs-option__label" });
      if (item.flagIso) {
        const flag = mk("span", { class: `locale-flag locale-flag--${item.flagIso}` });
        lbl.appendChild(flag);
      }
      lbl.appendChild(document.createTextNode(item.label));
      li.appendChild(lbl);
      if (item.sublabel) {
        const sub = mk("span", { class: "cs-option__sublabel" });
        sub.textContent = item.sublabel;
        li.appendChild(sub);
      }
      if (sel) {
        const chk = mk("span", { class: "cs-option__check", "aria-hidden": "true" });
        chk.innerHTML = CHECK_SVG;
        li.appendChild(chk);
      }
      this.listEl.appendChild(li);
    });
    this.activeIdx = -1;
  }

  private _refreshList(): void {
    const q = this.searchInput?.value.toLowerCase() ?? "";
    this._buildList(q ? this.allItems.filter(i => i.label.toLowerCase().includes(q)) : this.allItems);
  }

  private _wire(): void {
    this.trigger.addEventListener("click", () => this.isOpen ? this._close() : this._open());
    this.trigger.addEventListener("keydown", e => this._onTriggerKey(e));
    this.searchInput?.addEventListener("input", () => this._refreshList());
    this.searchInput?.addEventListener("keydown", e => this._onNavKey(e));
    this.listEl.addEventListener("click", e => {
      const li = (e.target as Element).closest<HTMLElement>(".cs-option");
      if (!li || li.classList.contains("cs-option--disabled") || li.classList.contains("cs-option--empty")) return;
      this._pick(li.dataset.value ?? "");
    });
    document.addEventListener("click", this._onDocClick, true);
    this.el.addEventListener("change", () => { this._updateLabel(); this._refreshList(); });
  }

  private _open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    // 포탈: overflow/clip 조상(인스펙터 스크롤 패널 등)을 벗어나도록 body로 옮기고
    // position:fixed 로 트리거 기준 배치 → 어떤 스크롤 컨테이너에도 잘리지 않는다.
    document.body.appendChild(this.dropdown);
    this.dropdown.classList.add("cs-dropdown--floating");
    this.dropdown.hidden = false;
    this.trigger.setAttribute("aria-expanded", "true");
    this.trigger.classList.add("cs-trigger--open");
    this._refreshList();
    this._position();
    window.addEventListener("scroll", this._onReposition, true);
    window.addEventListener("resize", this._onReposition);
    requestAnimationFrame(() => {
      if (this.searchInput) { this.searchInput.value = ""; this.searchInput.focus(); }
      this.listEl.querySelector(".cs-option--selected")?.scrollIntoView({ block: "nearest" });
      this._position();
    });
  }

  private _close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.dropdown.hidden = true;
    window.removeEventListener("scroll", this._onReposition, true);
    window.removeEventListener("resize", this._onReposition);
    // 포탈 원복 — 폼 구조/destroy 정합성 유지 및 인라인 좌표 초기화
    this.dropdown.classList.remove("cs-dropdown--floating", "cs-dropdown--up");
    const s = this.dropdown.style;
    s.position = s.top = s.left = s.bottom = s.minWidth = s.maxWidth = s.zIndex = "";
    this.container.appendChild(this.dropdown);
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.classList.remove("cs-trigger--open");
    this.activeIdx = -1;
    this._clearActive();
  }

  private _onReposition = (): void => {
    if (this.isOpen) this._position();
  };

  private _position(): void {
    const r = this.trigger.getBoundingClientRect();
    const dd = this.dropdown;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    dd.style.position = "fixed";
    // app-modal-backdrop(z-index:20000) 위로 — 모달 안 select 도 정상 표시.
    dd.style.zIndex = "20050";
    dd.style.minWidth = `${Math.round(r.width)}px`;
    dd.style.maxWidth = `${Math.round(vw - 16)}px`;
    const ddW = Math.max(dd.offsetWidth, r.width);
    const ddH = dd.offsetHeight;
    const forced = this.opts.openPosition ?? "auto";
    const spaceBelow = vh - r.bottom;
    const openUp = forced === "up"
      ? true
      : forced === "down"
        ? false
        : spaceBelow < Math.min(ddH + 8, 260) && r.top > spaceBelow;
    dd.classList.toggle("cs-dropdown--up", openUp);
    let left = r.left;
    if (left + ddW > vw - 8) left = Math.max(8, vw - 8 - ddW);
    dd.style.left = `${Math.round(left)}px`;
    if (openUp) {
      dd.style.top = "auto";
      dd.style.bottom = `${Math.round(vh - r.top + 4)}px`;
    } else {
      dd.style.bottom = "auto";
      dd.style.top = `${Math.round(r.bottom + 4)}px`;
    }
  }

  private _pick(value: string): void {
    const item = this.allItems.find(i => i.value === value);
    if (!item || item.disabled) return;
    this.el.value = value;
    this._updateLabel();
    this._close();
    this.el.dispatchEvent(new Event("change", { bubbles: true }));
    this.opts.onChange?.(value, item.label);
  }

  private _onTriggerKey(e: KeyboardEvent): void {
    if ([" ", "Enter", "ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      if (!this.isOpen) { this._open(); return; }
    }
    if (e.key === "Escape") { this._close(); this.trigger.focus(); return; }
    if (e.key === "ArrowDown") this._moveActive(1);
    else if (e.key === "ArrowUp") this._moveActive(-1);
    else if (e.key === "Enter") this._confirmActive();
  }

  private _onNavKey(e: KeyboardEvent): void {
    if (e.key === "Escape") { this._close(); this.trigger.focus(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); this._moveActive(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); this._moveActive(-1); }
    else if (e.key === "Enter") { e.preventDefault(); this._confirmActive(); }
  }

  private _moveActive(dir: 1 | -1): void {
    const items = [...this.listEl.querySelectorAll<HTMLElement>(".cs-option:not(.cs-option--disabled):not(.cs-option--empty)")];
    if (!items.length) return;
    this._clearActive();
    this.activeIdx = Math.max(0, Math.min(items.length - 1, this.activeIdx + dir));
    items[this.activeIdx].classList.add("cs-option--active");
    items[this.activeIdx].scrollIntoView({ block: "nearest" });
  }

  private _confirmActive(): void {
    const items = [...this.listEl.querySelectorAll<HTMLElement>(".cs-option:not(.cs-option--disabled):not(.cs-option--empty)")];
    const a = items[this.activeIdx];
    if (a) this._pick(a.dataset.value ?? "");
  }

  private _clearActive(): void {
    this.listEl.querySelectorAll(".cs-option--active").forEach(e => e.classList.remove("cs-option--active"));
  }

  private _onDocClick = (e: MouseEvent): void => {
    // dropdown 은 열려 있을 때 body 로 포탈되므로 container 뿐 아니라 dropdown 내부
    // 클릭도 "바깥"으로 오판하지 않도록 함께 검사한다.
    const target = e.target as Node;
    if (!this.container.contains(target) && !this.dropdown.contains(target)) this._close();
  };
}
